/**
 * **The edit bar** — one Save for every page that edits something (C13, spec
 * §10.5 item 3; ADR-0032 §5; interface I-1 of Wave 14).
 *
 * The owner, 2026-09-25: *"Navigation have fixed footer bar for buttons like
 * save, See it in your draft looks better, apply this to all other"*. So this
 * is Navigation's bar, written once: stuck to the bottom of the content, full
 * content width, **always rendered** on an edit page (a bar that appears only
 * when dirty moves the page under the thumb), and carrying:
 *
 * - **Save** — with a spinner while saving; disabled, with the reason as its
 *   title and in the state line, when nothing has changed or `problem` says
 *   something is wrong.
 * - **The state** — *Unsaved changes*, *Everything is saved*, *Not saved yet*
 *   (`isNew`: a thing that does not exist until the first Save, so Save is
 *   pressable before anything is typed), or the problem in the destructive tone.
 * - **Discard** — a ghost button, only while dirty and only when the page gives
 *   `onDiscard`.
 * - **See it in your draft ↗** — at the right, in a new tab, when the page gives
 *   `previewHref` (website pages; `useDraftPreviewHref()` reads it).
 *
 * A reader who may not edit (`editable={false}`) sees the bar without Save or
 * Discard and a *view only* line, so the page does not change shape by role.
 *
 * Pages that save per item (Pictures, the Branches table) keep their per-item
 * actions and show no bar (§10.5 item 3).
 */

import { Button } from '@fiveox/ui'
import { CircleAlert, CircleCheck, CircleDot, ExternalLink, Eye } from 'lucide-react'

export interface EditBarProps {
  /** Is there anything on the page that Save would send? */
  dirty: boolean
  /** A save is in flight. */
  saving: boolean
  onSave: () => void
  /** Put the page back to what is saved. Discard is shown only when given. */
  onDiscard?: () => void
  /** Why Save is refused before any request — shown in place of the state. */
  problem?: string | null
  /** The draft preview, opened in a new tab. Website pages only. */
  previewHref?: string | null
  /** Defaults to *Save*. */
  saveLabel?: string
  /** `false` for a reader without the verb: no Save, no Discard. Default true. */
  editable?: boolean
  /** The item has never been saved (a new post): the bar says *Not saved yet*
   *  rather than *Everything is saved*, and Save may be pressed while clean. */
  isNew?: boolean
}

/** The line the bar shows, and the reason Save is disabled — pure, so the
 *  states are testable without mounting. */
export function editBarState({
  dirty,
  problem,
  editable = true,
  isNew = false,
}: Pick<EditBarProps, 'dirty' | 'problem' | 'editable' | 'isNew'>): {
  tone: 'problem' | 'dirty' | 'new' | 'saved' | 'readonly'
  text: string
  /** Why Save is disabled, or `null` when it may be pressed. */
  refused: string | null
} {
  if (!editable) {
    return { tone: 'readonly', text: 'View only — you can’t change this.', refused: 'View only' }
  }
  if (problem) return { tone: 'problem', text: problem, refused: problem }
  if (dirty) return { tone: 'dirty', text: 'Unsaved changes', refused: null }
  if (isNew) return { tone: 'new', text: 'Not saved yet', refused: null }
  return { tone: 'saved', text: 'Everything is saved', refused: 'Nothing has changed yet' }
}

export function EditBar({
  dirty,
  saving,
  onSave,
  onDiscard,
  problem = null,
  previewHref = null,
  saveLabel = 'Save',
  editable = true,
  isNew = false,
}: EditBarProps) {
  const state = editBarState({ dirty, problem, editable, isNew })

  return (
    <div
      data-edit-bar=""
      data-state={state.tone}
      className="border-border bg-background/95 sticky bottom-0 z-30 mt-2 border-t py-3 backdrop-blur"
    >
      <div className="flex flex-wrap items-center gap-x-3 gap-y-2">
        {editable && (
          <Button
            onClick={onSave}
            loading={saving}
            loadingText="Saving…"
            disabled={saving || state.refused !== null}
            title={state.refused ?? undefined}
          >
            {saveLabel}
          </Button>
        )}
        {editable && dirty && onDiscard && (
          <Button variant="ghost" onClick={onDiscard} disabled={saving}>
            Discard
          </Button>
        )}
        <p
          role="status"
          aria-live="polite"
          className={`flex min-w-0 items-center gap-1.5 text-sm ${
            state.tone === 'problem' ? 'text-destructive' : 'text-muted-foreground'
          }`}
        >
          {state.tone === 'problem' ? (
            <CircleAlert size={14} className="shrink-0" aria-hidden />
          ) : state.tone === 'dirty' || state.tone === 'new' ? (
            <CircleDot size={14} className="text-primary shrink-0" aria-hidden />
          ) : state.tone === 'saved' ? (
            <CircleCheck size={14} className="text-primary shrink-0" aria-hidden />
          ) : (
            <Eye size={14} className="shrink-0" aria-hidden />
          )}
          <span className="min-w-0">{state.text}</span>
        </p>
        {previewHref && (
          <a
            href={previewHref}
            target="_blank"
            rel="noopener"
            data-edit-bar-preview=""
            className="text-primary ml-auto inline-flex items-center gap-1 text-sm font-medium hover:underline"
          >
            See it in your draft <ExternalLink size={14} aria-hidden />
          </a>
        )}
      </div>
    </div>
  )
}
